import _ from 'lodash';

import fromSmoochMessage from 'adapters/converters/fromSmoochMessage';
import generateId from 'lib/generateId';
import getCustomerProperties from 'lib/getCustomerProperties';
import getTrimmedUrl from 'lib/getTrimmedUrl';
import MessageGatewayBase from 'adapters/gateways/MessageGatewayBase';
import { SUB_TYPES } from 'app/modules/messages/constants';
import { TypingState } from 'app/modules/session/constants';

export default class SmoochMessageGateway extends MessageGatewayBase {
  constructor(smooch, sessionStorage) {
    super();
    this.sessionStorage = sessionStorage;
    this.smooch = smooch;
    this.isActiveSession = false;
    this.lastMessageReceivedAt = 0;
    this._registerMessageCallbacks();
    this._registerTypingActivityCallbacks();
    this._customerProperties = getCustomerProperties();
  }

  configure({ orgDomain, loginDomain, appId }) {
    this.orgDomain = orgDomain;
    this.loginDomain = loginDomain;
    this.appId = appId;
  }

  setGladlyCustomerId(gladlyCustomerId) {
    this.gladlyCustomerId = gladlyCustomerId;
  }

  requestSession() {
    let conversation = this.smooch.getConversation();
    if (conversation) {
      this._handleFetchConversation(conversation);
      this._handleFetchMessages(conversation.messages);
    } else {
      this.notifyObservers('handleRequestSessionError', {});
    }
  }

  onFetchResponse() {}

  onFetchError() {}

  sendMessage(messageText) {
    if (!this.gladlyCustomerId) {
      throw new Error('GladlyCustomerId must be set before sending message');
    }

    let { browser, device } = this._customerProperties;
    let trimmedUrl = getTrimmedUrl();
    let internalId = generateId();
    let smoochMessage = {
      metadata: {
        gladlyCustomerId: this.gladlyCustomerId,
        internalId: internalId,
        isInitialMessage: !this.isActiveSession,
        subType: SUB_TYPES.TEXT,
        url: trimmedUrl,
        browser,
        device,
      },
      text: messageText,
      type: 'text',
      received: Date.now() / 1000,
      role: 'appUser',
    };
    let message = fromSmoochMessage(smoochMessage);
    let session = { isEnded: false, metadata: { endedReason: '' } };
    if (!this.isActiveSession) {
      session.customerUrl = trimmedUrl;
    }
    this.isActiveSession = true;

    this.notifyObservers('handleSessionUpdated', session);
    this.smooch.sendMessage(smoochMessage);
    this.notifyObservers('handleSendMessage', message);
  }

  onSendMessageSuccess(message) {
    // we send a message when we update the customer URL and do not want this to show up in the feed
    if (message.metadata.subType === SUB_TYPES.TEXT) {
      this.notifyObservers('handleSendMessageSuccess', fromSmoochMessage(message));
    }

    // A Smooch conversation does not have an ID until the first message has been sent.
    // The session must have an ID to be considered active.
    // Note: consider refactoring this to have a more explicit check if a session is active or not.
    let conversation = this.smooch.getConversation();
    if (conversation) {
      let session = { id: conversation._id };
      this.notifyObservers('handleSessionUpdated', session);
    }
  }

  onMessageReceived(message) {
    if (message.received <= this.lastMessageReceivedAt) {
      return;
    }
    this.lastMessageReceivedAt = message.received;
    if (message.metadata.subType === SUB_TYPES.SESSION_END) {
      let session = { isEnded: true, isAgentTyping: false, metadata: { endedReason: message.text } };
      this.isActiveSession = false;
      this.notifyObservers('handleSessionUpdated', session);
    } else if (message.metadata.subType === SUB_TYPES.TEXT) {
      this.notifyObservers('handleMessageReceived', fromSmoochMessage(message));
    }
  }

  handleEntity() {}

  updateCustomerUrl(customerUrl) {
    if (this.isActiveSession) {
      this.smooch.sendMessage({
        metadata: {
          gladlyCustomerId: this.gladlyCustomerId,
          subType: SUB_TYPES.URL,
          url: customerUrl,
        },
        text: `URL_NAVIGATED`,
        type: 'text',
      });
    }
  }

  updateCustomerTypingState(typingState) {
    if (this.isActiveSession) {
      typingState === TypingState.STARTED ? this.smooch.startTyping() : this.smooch.stopTyping();
    }
  }

  updateWelcomeMessage(welcomeMessage) {
    this.sessionStorage.patchSession({ welcomeMessage: welcomeMessage });
  }

  _registerTypingActivityCallbacks() {
    this.smooch.on('typing:start', () => {
      if (this.isActiveSession) {
        this.notifyObservers('handleSessionUpdated', {
          isAgentTyping: true,
        });
      }
    });

    this.smooch.on('typing:stop', () => {
      if (this.isActiveSession) {
        this.notifyObservers('handleSessionUpdated', { isAgentTyping: false });
      }
    });
  }

  _registerMessageCallbacks() {
    this.smooch.on('message:sent', message => {
      this.onSendMessageSuccess(message);
    });

    this.smooch.on('message:received', message => {
      this.onMessageReceived(message);
    });
  }

  _handleFetchMessages(messages) {
    let textMessages = _.filter(messages, m => {
      if (m.metadata.subType == SUB_TYPES.TEXT) {
        return m;
      }
    });
    textMessages = textMessages.map(m => fromSmoochMessage(m));
    if (textMessages.length) {
      this.notifyObservers('handleFetchSuccess', textMessages);
    }
  }

  _handleFetchConversation(conversation) {
    let messages = conversation.messages;
    let session = { id: conversation._id };
    let customerIndex = _.findLastIndex(messages, m => {
      return m.role === 'appUser' && m.metadata.subType === SUB_TYPES.TEXT;
    });
    let endIndex = _.findLastIndex(messages, m => {
      return m.metadata.subType === SUB_TYPES.SESSION_END;
    });
    let lastUrlMessage = _.findLast(messages, m => m.metadata.subType === SUB_TYPES.URL);
    let firstMessageUrl = messages[endIndex + 1] && messages[endIndex + 1].metadata.url;
    let lastUrl = lastUrlMessage ? lastUrlMessage.metadata.url : firstMessageUrl;
    session.customerUrl = lastUrl;

    if (endIndex <= customerIndex) {
      session.isEnded = false;
    } else {
      session.isEnded = true;
      session.metadata = { endedReason: messages[endIndex].text };
    }

    // if there are customer messages and the session is active
    if (customerIndex !== -1 && !session.isEnded) {
      this.isActiveSession = true;
    }

    let storedSession = this.sessionStorage.getSession();
    if (storedSession && storedSession.welcomeMessage) {
      session.welcomeMessage = storedSession.welcomeMessage;
    }

    if (messages.length) {
      this.lastMessageReceivedAt = messages[messages.length - 1].received;
    }
    this.notifyObservers('handleRequestSessionSuccess', session);
  }
}
